import { Phone, Mail, MessageCircle, MapPin, ChevronUp } from "lucide-react";
import { ShareActions } from "@/components/ShareActions";

const ContactRow = ({ icon: Icon, label, value, href, testId }) => {
  const inner = (
    <>
      <span className="flex h-[6.4cqh] w-[6.4cqh] shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/[0.08] text-gold">
        <Icon className="h-[42%] w-[42%]" strokeWidth={1.75} />
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="text-[1.65cqh] uppercase tracking-[0.3em] text-white/40">
          {label}
        </span>
        <span className="truncate text-[2.45cqh] font-medium tracking-wide text-white/90">
          {value}
        </span>
      </span>
    </>
  );

  const className =
    "group flex items-center gap-[3.5cqw] rounded-xl border border-white/[0.06] bg-white/[0.03] px-[4cqw] py-[1.2cqh] transition-colors duration-300 hover:border-gold/30 hover:bg-white/[0.05]";

  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        className={className}
        onClick={(e) => e.stopPropagation()}
        {...(href.startsWith("http")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId} className={className}>
      {inner}
    </div>
  );
};

export const CardBack = ({ founder, active = false }) => {
  return (
    <div
      data-testid="card-back"
      className={`card-face absolute inset-0 select-none ${active ? "" : "pointer-events-none"}`}
      style={{ transform: "rotateY(180deg) translateZ(1px)" }}
    >
      <div className="pointer-events-none absolute inset-3 rounded-2xl border border-gold/25" />
      <div className="pointer-events-none absolute inset-[15px] rounded-xl border border-white/[0.05]" />

      <div className="relative z-10 flex h-full flex-col px-[7cqw] pb-[5cqh] pt-[5.5cqh]">
        <header className="flex flex-col items-center gap-[1.4cqh]">
          <p className="pl-[0.4em] text-[1.85cqh] font-medium tracking-[0.4em] text-gold">
            ASCEND FINANCE GROUP
          </p>
          <span className="h-px w-14 bg-gold/40" />
        </header>

        <div className="mt-[2.6cqh] flex flex-col items-center">
          <h1
            data-testid="founder-name"
            className="font-display text-[5.8cqh] font-semibold leading-[1.15] text-white"
          >
            {founder.name}
          </h1>
          <p className="mt-[0.6cqh] pl-[0.35em] text-[1.85cqh] tracking-[0.35em] text-gold/90">
            {founder.designation.toUpperCase()}
          </p>
        </div>

        <div className="my-[2.4cqh] flex items-center gap-3 px-4" aria-hidden="true">
          <span className="h-px flex-1 bg-white/10" />
          <span className="h-1.5 w-1.5 rotate-45 border border-gold/60" />
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="flex flex-col gap-[1.3cqh]">
          <ContactRow
            icon={Phone}
            label="Phone"
            value={founder.phoneDisplay}
            href={`tel:${founder.phone}`}
            testId="phone-link"
          />
          <ContactRow
            icon={Mail}
            label="Email"
            value={founder.email}
            href={`mailto:${founder.email}`}
            testId="email-link"
          />
          <ContactRow
            icon={MessageCircle}
            label="WhatsApp"
            value={founder.whatsappDisplay}
            href={`https://wa.me/${founder.whatsapp}`}
            testId="whatsapp-link"
          />
          <ContactRow
            icon={MapPin}
            label="Location"
            value={founder.location}
            testId="location-row"
          />
        </div>

        <ShareActions founder={founder} />

        <div
          data-testid="swipe-cue"
          className="mt-auto flex flex-col items-center gap-[0.4cqh] pt-[1.2cqh]"
          aria-hidden="true"
        >
          <ChevronUp
            strokeWidth={1.75}
            className="h-[3.2cqh] w-[3.2cqh] text-gold animate-cue-rise"
          />
          <span className="pl-[0.4em] text-[1.65cqh] tracking-[0.4em] text-white/50">
            SWIPE UP TO VISIT ASCEND
          </span>
        </div>
      </div>
    </div>
  );
};
